import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { obligationAuthorityLabel } from "@/modules/despacho/despachoFiscalInfoHelpers";

function normalizeAuthority(value) {
  const raw = String(value || "").trim();
  return raw || "Otras";
}

export default function ObligationCatalogPicker({
  catalog = [],
  selectedIds = [],
  onToggle,
  readOnly = false,
  testId = "obligation-catalog-picker",
}) {
  const [search, setSearch] = useState("");
  const [authorityTab, setAuthorityTab] = useState("todas");

  const enriched = useMemo(
    () =>
      (catalog || []).map((row) => ({
        id: row.id,
        label: row.nombre_visible || row.short_name || row.id,
        authority: normalizeAuthority(row.authority),
      })),
    [catalog],
  );

  const authorities = useMemo(() => {
    const set = new Set(enriched.map((r) => r.authority));
    return ["todas", ...[...set].sort((a, b) => a.localeCompare(b, "es"))];
  }, [enriched]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter((row) => {
      if (authorityTab !== "todas" && row.authority !== authorityTab) return false;
      if (!q) return true;
      return (
        row.label.toLowerCase().includes(q)
        || row.id.toLowerCase().includes(q)
        || row.authority.toLowerCase().includes(q)
      );
    });
  }, [authorityTab, enriched, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of filtered) {
      if (!map.has(row.authority)) map.set(row.authority, []);
      map.get(row.authority).push(row);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], "es"));
  }, [filtered]);

  const selectedRows = useMemo(
    () => enriched.filter((r) => selectedSet.has(r.id)),
    [enriched, selectedSet],
  );

  return (
    <div className="space-y-3" data-testid={testId}>
      <div>
        <p className="text-xs font-semibold text-[#0F2D5C] m-0 mb-2">
          Obligaciones seleccionadas ({selectedRows.length})
        </p>
        <div className="flex flex-wrap gap-2 min-h-[28px]">
          {selectedRows.length ? (
            selectedRows.map((row) => (
              <span
                key={row.id}
                className="inline-flex items-center gap-1 rounded-sm border border-[#0F2D5C]/20 bg-[#0F2D5C]/5 px-2 py-1 text-xs"
              >
                {row.label}
                {!readOnly ? (
                  <button
                    type="button"
                    className="text-slate-500 hover:text-rose-700 font-bold leading-none"
                    onClick={() => onToggle(row.id, false)}
                    aria-label={`Quitar ${row.label}`}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            ))
          ) : (
            <span className="text-xs text-slate-500">
              Ninguna — marque obligaciones en el catálogo abajo.
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-2">
        {authorities.map((auth) => (
          <button
            key={auth}
            type="button"
            className={`rounded-sm px-2 py-1 text-[11px] border ${
              authorityTab === auth
                ? "bg-[#0F2D5C] text-white border-[#0F2D5C]"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
            onClick={() => setAuthorityTab(auth)}
          >
            {auth === "todas" ? "Todas" : obligationAuthorityLabel(auth) || auth}
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" aria-hidden />
        <input
          className="flex h-9 w-full rounded-sm border border-slate-200 pl-8 pr-3 text-sm"
          placeholder="Buscar obligación…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="max-h-72 overflow-y-auto rounded-sm border border-slate-200 divide-y divide-slate-100">
        {grouped.length ? (
          grouped.map(([authority, rows]) => (
            <div key={authority} className="p-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0 mb-2">
                {obligationAuthorityLabel(authority) || authority}
              </p>
              <ul className="space-y-1.5 m-0 p-0 list-none">
                {rows.map((row) => {
                  const checked = selectedSet.has(row.id);
                  const inputId = `${testId}-${row.id}`;
                  return (
                    <li key={row.id}>
                      <label
                        htmlFor={inputId}
                        className={`flex items-start gap-2 rounded-sm px-2 py-1.5 text-sm ${
                          readOnly ? "cursor-default" : "cursor-pointer hover:bg-slate-50"
                        }`}
                      >
                        <Checkbox
                          id={inputId}
                          checked={checked}
                          disabled={readOnly}
                          onCheckedChange={(value) => onToggle(row.id, value === true)}
                          className="mt-0.5"
                        />
                        <span className="flex-1">
                          <span className="block text-slate-800">{row.label}</span>
                          <span className="block text-[10px] text-slate-500">{row.id}</span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        ) : (
          <p className="text-sm text-slate-500 m-0 p-4">No hay obligaciones que coincidan con el filtro.</p>
        )}
      </div>

      {!readOnly ? (
        <p className="text-[10px] text-slate-500 m-0">
          Solo agrupe obligaciones que vencen el <strong>mismo día del mes</strong>. Guarde el grupo tras seleccionar.
        </p>
      ) : null}
    </div>
  );
}
